import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useSession } from './session';

/**
 * Global Realtime database listener for Supabase.
 * Listens for ANY Postgres INSERT, UPDATE, or DELETE on public schema tables
 * and immediately triggers React Query cache invalidations so that all tables
 * and views across the app refresh instantly without requiring manual page refresh.
 */
export function useRealtimeSync() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  useEffect(() => {
    if (!actor?.companyId) return;

    const channel = supabase
      .channel('vns-realtime-db-sync')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public' },
        (payload) => {
          const table = payload.table;

          // Target specific queries based on database table changes
          if (table === 'expenses' || table === 'expense_categories') {
            void qc.invalidateQueries({ queryKey: ['expense-transactions'] });
            void qc.invalidateQueries({ queryKey: ['expenses'] });
            void qc.invalidateQueries({ queryKey: ['project'] });
            void qc.invalidateQueries({ queryKey: ['projects'] });
            void qc.invalidateQueries({ queryKey: ['dashboard'] });
            void qc.invalidateQueries({ queryKey: ['lookups'] });
          } else if (table === 'client_payments' || table === 'clients') {
            void qc.invalidateQueries({ queryKey: ['client-transactions'] });
            void qc.invalidateQueries({ queryKey: ['project-client-payment-summary'] });
            void qc.invalidateQueries({ queryKey: ['clients'] });
            void qc.invalidateQueries({ queryKey: ['projects'] });
            void qc.invalidateQueries({ queryKey: ['project'] });
            void qc.invalidateQueries({ queryKey: ['dashboard'] });
            void qc.invalidateQueries({ queryKey: ['lookups'] });
          } else if (
            table === 'purchase_bills' ||
            table === 'material_purchases' ||
            table === 'supplier_payments' ||
            table === 'suppliers'
          ) {
            void qc.invalidateQueries({ queryKey: ['supplier-transactions'] });
            void qc.invalidateQueries({ queryKey: ['supplier-balance-summary'] });
            void qc.invalidateQueries({ queryKey: ['bills'] });
            void qc.invalidateQueries({ queryKey: ['suppliers'] });
            void qc.invalidateQueries({ queryKey: ['project'] });
            void qc.invalidateQueries({ queryKey: ['projects'] });
            void qc.invalidateQueries({ queryKey: ['dashboard'] });
            void qc.invalidateQueries({ queryKey: ['materials'] });
            void qc.invalidateQueries({ queryKey: ['lookups'] });
          } else if (
            table === 'subcontractor_labour_batches' ||
            table === 'subcontractor_labour' ||
            table === 'subcontractor_payments' ||
            table === 'subcontractor_contracts' ||
            table === 'subcontractors'
          ) {
            void qc.invalidateQueries({ queryKey: ['subcontractor-transactions'] });
            void qc.invalidateQueries({ queryKey: ['subcontractor-balance-summary'] });
            void qc.invalidateQueries({ queryKey: ['subcontractor-contracts'] });
            void qc.invalidateQueries({ queryKey: ['subcontractors'] });
            void qc.invalidateQueries({ queryKey: ['project'] });
            void qc.invalidateQueries({ queryKey: ['projects'] });
            void qc.invalidateQueries({ queryKey: ['dashboard'] });
            void qc.invalidateQueries({ queryKey: ['lookups'] });
          } else if (table === 'material_usages' || table === 'materials') {
            void qc.invalidateQueries({ queryKey: ['materials'] });
            void qc.invalidateQueries({ queryKey: ['usage'] });
            void qc.invalidateQueries({ queryKey: ['stock-transfers'] });
            void qc.invalidateQueries({ queryKey: ['project'] });
            void qc.invalidateQueries({ queryKey: ['projects'] });
            void qc.invalidateQueries({ queryKey: ['lookups'] });
          } else if (table === 'projects') {
            void qc.invalidateQueries({ queryKey: ['projects'] });
            void qc.invalidateQueries({ queryKey: ['project'] });
            void qc.invalidateQueries({ queryKey: ['dashboard'] });
            void qc.invalidateQueries({ queryKey: ['lookups'] });
          } else if (table === 'bank_accounts' || table === 'fund_transfers') {
            void qc.invalidateQueries({ queryKey: ['bank-accounts'] });
            void qc.invalidateQueries({ queryKey: ['fund-transfers'] });
            void qc.invalidateQueries({ queryKey: ['dashboard'] });
          } else {
            // General catch-all invalidation
            void qc.invalidateQueries();
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [actor?.companyId, qc]);
}

/**
 * Component that activates the RealtimeSync hook at the app root
 */
export function RealtimeSync() {
  useRealtimeSync();
  return null;
}
